import { useCallback, useEffect, useRef } from 'react';

export const useSupportKeyboardFocus = ({ rootRef, open, menuType }) => {
  const handleItemFocus = useHandleItemsFocus({ rootRef, menuType });
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleDocumentKeyDown = (event) => {
      if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.stopPropagation();
        handleItemFocus({ pressedKey: event.key });
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [open, handleItemFocus]);
};

const useHandleItemsFocus = ({ rootRef, menuType }) => {
  const focusedElementRef = useRef(null);

  // Reset "focused id" when "menuType" changes, so we always start from the first item
  useEffect(() => {
    focusedElementRef.current = null;
  }, [menuType]);

  return useCallback(
    ({ pressedKey }) => {
      if (!rootRef.current) {
        return;
      }

      const items = [...rootRef.current.querySelectorAll('ul > li:not([disabled])')];
      const firstItem = items[0];
      const lastItem = items[items.length - 1];

      if (!firstItem || !lastItem) {
        focusedElementRef.current = null;
        return;
      }

      if (!focusedElementRef.current) {
        firstItem.focus();
        focusedElementRef.current = firstItem;
        return;
      }

      const focusedItemIndex = items.indexOf(focusedElementRef.current);
      if (focusedItemIndex === -1) {
        firstItem.focus();
        focusedElementRef.current = firstItem;
        return;
      }

      if (pressedKey === 'ArrowDown') {
        const nextItem = items[focusedItemIndex + 1];
        if (nextItem) {
          nextItem.focus();
          focusedElementRef.current = nextItem;
        } else {
          firstItem.focus();
          focusedElementRef.current = firstItem;
        }
      }

      if (pressedKey === 'ArrowUp') {
        const prevItem = items[focusedItemIndex - 1];
        if (prevItem) {
          prevItem.focus();
          focusedElementRef.current = prevItem;
        } else {
          lastItem.focus();
          focusedElementRef.current = lastItem;
        }
      }
    },
    [rootRef]
  );
};
